import Parsers from "./parsers/Parsers.js";
import Transaction from "./Transaction.js";
import CurrencyService from "./fx/CurrencyService.js";

/**
 *
 * @version 2026-09-04
 * @author Patrik Harag
 */
export default class Dataset {

    /** @type {string} */
    #name;

    /** @type {string} */
    #dataType;

    /** @type {string} */
    #data;

    /** @type {Transaction[]} */
    #transactions = [];

    /** @type {*|null} */
    #exception = null;

    /** @type {{title: string, e: *}[]} */
    #problems = [];

    /** @type {string} */
    #currency;

    /**
     *
     * @param name {string}
     * @param dataType {string}
     * @param data {string}
     * @param currency {string|null}
     */
    constructor(name, dataType, data, currency = null) {
        if (name === undefined || name === null) {
            throw "Dataset name not set";
        }
        if (dataType === undefined || dataType === null) {
            throw "Dataset dataType not set";
        }
        if (data === undefined || data === null) {
            throw "Dataset data not set";
        }
        this.#name = name;
        this.#dataType = dataType;
        this.#data = data;
        this.#currency = (currency !== null && currency !== undefined && currency !== '')
                ? currency
                : CurrencyService.getDefaultCurrency();
    }

    getName() {
        return this.#name;
    }

    getDataType() {
        return this.#dataType;
    }

    getData() {
        return this.#data;
    }

    getCurrency() {
        return this.#currency;
    }

    /**
     *
     * @return {Transaction[]}
     */
    getTransactions() {
        return this.#transactions;
    }

    getException() {
        return this.#exception;
    }

    /**
     *
     * @return {{title: string, e: *}[]}
     */
    getProblems() {
        return this.#problems;
    }

    /**
     *
     * @param enableUrlDatasets {boolean}
     * @param logger {Logger}
     * @return {Promise<array>}
     */
    loadTransactions(enableUrlDatasets, logger) {
        this.#exception = null;
        this.#problems = [];
        this.#transactions = [];

        let multiTransactionNumber = 0;
        let multiTransactionNumberProduced = () => multiTransactionNumber++;

        if (enableUrlDatasets && Dataset.#isValidHttpUrl(this.#data)) {
            // asynchronously fetch raw data from url and parse
            return fetch(this.#data)
                .then(response => {
                    if (!response.ok) {
                        let statusText = (typeof response.statusText === 'string') ? response.statusText.trim() : '';
                        let message = 'HTTP ' + response.status;
                        if (statusText !== '') {
                            message += ' ' + statusText;
                        }
                        throw message + ': ' + this.#data;
                    }
                    return response.text();
                })
                .then(result => {
                    try {
                        this.#transactions = this.#parse(result, multiTransactionNumberProduced, logger);
                    } catch (e) {
                        this.#exception = e;
                        this.#transactions = [];
                        throw e;
                    }
                    return this.#transactions;
                })
                .catch(e => {
                    if (this.#exception === null) {
                        this.#exception = e;
                    }
                    throw e;
                });
        } else {
            // parse only
            return new Promise((resolve, reject) => {
                try {
                    this.#transactions = this.#parse(this.#data, multiTransactionNumberProduced, logger);
                } catch (e) {
                    this.#exception = e;
                    this.#transactions = [];
                    reject(e);
                    return;
                }
                resolve(this.#transactions);
            });
        }
    }

    #parse(data, multiTransactionNumberProduced, logger) {
        let parser = Parsers.resolveParserByKey(this.#dataType);
        let transactions = parser.parse(data, logger);

        let copy = [];
        for (let transaction of transactions) {
            transaction = new Transaction(
                    transaction.getDate(),
                    transaction.getDescription(),
                    transaction.getValue(),
                    this.#name,
                    null,
                    this.#currency);

            if (transaction.getDescription().includes(';;;')) {
                // process multi-transaction
                let multiTransactionNumber = multiTransactionNumberProduced();
                copy.push(...this.#splitOrFallbackMultiTransaction(transaction, multiTransactionNumber));
            } else {
                copy.push(transaction);
            }
        }
        return copy;
    }

    #splitOrFallbackMultiTransaction(transaction, multiTransactionNumber) {
        try {
            return this.#splitMultiTransaction(transaction, multiTransactionNumber);
        } catch (e) {
            this.#problems.push({
                title: "Dataset '" + this.#name + "' multi-transaction failed",
                e: e
            });
            return [this.#fallbackMultiTransaction(transaction)];
        }
    }

    /**
     *
     * @param transaction {Transaction}
     * @return {Transaction}
     */
    #fallbackMultiTransaction(transaction) {
        let generalText = transaction.getDescription().split(';;;')[0].trim();
        return new Transaction(
                transaction.getDate(),
                generalText,
                transaction.getValue(),
                transaction.getDataset(),
                null,
                transaction.getCurrency());
    }

    #splitMultiTransaction(transaction, multiTransactionNumber) {
        let result = [];

        // General text ;;; 100 ; Payment 1 ;;; 200 ; Payment 2

        let parts = transaction.getDescription().split(';;;');

        // normalize general text
        let generalText = parts.shift();
        generalText = generalText.trim();
        if (generalText !== '') {
            generalText += '; ';
        } else {
            generalText = '';
        }

        // process parts
        for (let part of parts) {
            part = part.trim();
            if (part) {
                let separatorPos = part.indexOf(';');
                if (separatorPos && separatorPos + 1 < part.length) {
                    const numberPart = part.substring(0, separatorPos).trim();
                    const descriptionPart = part.substring(separatorPos + 1).trim();
                    result.push(new Transaction(
                            transaction.getDate(),
                            generalText + descriptionPart + ' [M-' + multiTransactionNumber + ']',
                            parseFloat(numberPart),
                            transaction.getDataset(),
                            transaction,
                            transaction.getCurrency()));
                } else {
                    throw 'Multi-transaction failed: ' + transaction.getDescription();
                }
            }
        }

        // test
        let sum = result.reduce((sum, t) => sum + t.getValue(), 0);
        if (sum !== transaction.getValue()) {
            throw `Multi-transaction checksum failed (${sum}): ${ transaction.getDescription() }`;
        }

        return result;
    }

    static #isValidHttpUrl(string) {
        if (string.trim().includes('\n')) {
            return false;
        }

        let url;
        try {
            url = new URL(string);
        } catch (_) {
            return false;
        }

        return url.protocol === "http:" || url.protocol === "https:";
    }

    /**
     *
     * @param object
     * @return {Dataset}
     */
    static asDataset(object) {
        let dataset = new Dataset(object.name, object.dataType, object.data, object.currency);
        if (object.transactions !== undefined && object.transactions !== null) {
            dataset.#transactions = object.transactions;
        }
        return dataset;
    }

    /**
     *
     * @param jsonString {string}
     * @returns {Map<string,Dataset>}
     */
    static parseDatasets(jsonString) {
        let parsed = JSON.parse(jsonString);
        let datasets = new Map();
        for (const [key, value] of Object.entries(parsed)) {
            datasets.set(key, Dataset.asDataset(value));
        }
        return datasets;
    }

    /**
     *
     * @param datasets {Map<string,Dataset>}
     * @returns {string} json string
     */
    static serializeDatasets(datasets) {
        let copy = {};
        datasets.forEach(d => {
            copy[d.getName()] = {
                name: d.getName(),
                dataType: d.getDataType(),
                data: d.getData(),
                currency: d.getCurrency(),
            };
        });
        return JSON.stringify(copy);
    }

    /**
     *
     * @param datasets {Map<string,Dataset>}
     * @returns {Map<string,Dataset>} deep copy
     */
    static copyDatasets(datasets) {
        let copy = new Map();
        datasets.forEach(d => {
            copy.set(d.getName(), new Dataset(d.getName(), d.getDataType(), d.getData(), d.getCurrency()));
        });
        return copy;
    }
}
